import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ConversationMessage, EnhancedToolResult, ToolExecutionContext } from '../types';
import { getSkillsInContext } from '../skills/loadedSkills';
import type { ToolConstructorArgs } from '../tools/Tool';
import { DesignTool, designTargetSchemaFields } from './designTargeting';
import type { TechniqueAssignment } from './skills/suggestTechniques';
import { assignmentFor, fallbackAssignment, shortlist } from './skills/suggestTechniques';
import { chooseTechniques } from './skills/chooseTechniques';
import { scheduleRejection } from './phaseSchedule';

import { markScenesBusy } from 'js/src/data/agent-activity';
import {
    getAssignedTechniques,
    getEarlierScenePlans,
    setHtmlDesignPlan,
} from 'js/src/data/html-design';
import { setSceneDurationUserOverride } from 'js/src/data/user-overrides';
import { setSceneDuration } from 'js/src/luminary/utils';
import { isVoicedrivenScene } from 'js/src/luminary/voicedriven-scenes';

export type { ScenePlan } from 'js/src/data/html-design';

export const writePlanSchema = z.object({
    ...designTargetSchemaFields,
    sceneDurationMillis: z
        .number()
        .describe(
            'How long the whole scene runs, in milliseconds. You pace the ' +
                'graphic within it when you author the timeline.',
        ),
    scenePlan: z
        .object({
            text: z
                .array(z.string(), {
                    // A chart plan tends to send its figures in `data` and leave this out, then,
                    // told only that an array was expected, resend the plan with `data` dropped.
                    error: (issue) =>
                        issue.input === undefined
                            ? "required even beside data: list the words the graphic shows, a chart's labels and figures included, or [] if it shows none"
                            : undefined,
                })
                .describe(
                    'The exact words of every text block the graphic shows — ' +
                        'one entry per block, written out, not described. Always ' +
                        'send this field: pass [] for a graphic that shows no text. ' +
                        'Read the list back before ' +
                        'submitting: no figure and no claim may appear in more ' +
                        'than one entry.',
                ),
            graphics: z
                .array(z.string())
                .describe(
                    'Each non-text element, one per entry (e.g. "person icon", ' +
                        '"filled person icon"). Each one has to show something nothing else on ' +
                        "screen does — any line drawn along a chart's series or across or " +
                        'between its bars (an arrow, a bracket, a guide line) restates what the ' +
                        'chart already shows, and reads as noise. A rise or fall is performed, ' +
                        'not drawn: a figure that falls counts down, a bar that shrinks shrinks, ' +
                        'a thing that travels moves — never a figure slid down the frame, since ' +
                        'position on screen is layout, not value. An arrow is for "leads to" or ' +
                        '"this one", never for "went up" or "went down".',
                ),
            layout: z
                .string()
                .describe('Where those elements sit, e.g. "text on left, graphic on ' + 'right".'),
            data: z
                .array(
                    z.object({
                        label: z.string().describe('What this series is, e.g. "balance by year".'),
                        values: z
                            .array(z.number())
                            .describe('The real figures, in plotting order.'),
                    }),
                )
                .optional()
                .describe(
                    'The figures the graphic plots — chart, bar set, counter or unit field — in ' +
                        'plotting order, as the narration gives them; its shape is checked ' +
                        'against them. A stand-in like 1,2,3 declares nothing: an illustrative ' +
                        'shape takes data-l5-not-data instead.',
                ),
            phases: z
                .array(
                    z.object({
                        at: z
                            .number()
                            .describe(
                                'When this phase begins, in seconds from the start of the scene — ' +
                                    'the same clock the narration timings use, so a phase can be ' +
                                    'placed on the word it illustrates, never before the ' +
                                    'narration has named it. The first phase is at 0.',
                            ),
                        beat: z
                            .string()
                            .describe(
                                "The moment this phase lands, in the narration's own words (e.g. " +
                                    '"...only three had shipped"). Plain description of the beat ' +
                                    'for a scene with no narration.',
                            ),
                        enters: z
                            .string()
                            .optional()
                            .describe(
                                'The ONE new element arriving in this phase. If a phase needs ' +
                                    'three new elements before it makes sense, it is a second ' +
                                    "idea wearing a phase's clothes — split it or cut it. Omit it " +
                                    'only for a phase that brings nothing new — a settle, or a ' +
                                    'rearrangement of what is already there — which then has to ' +
                                    'say what it does in transforms or exits.',
                            ),
                        transforms: z
                            .string()
                            .optional()
                            .describe(
                                'What is already on screen and changes to make room — only an ' +
                                    'element the arrival is read against, e.g. "the chart slides ' +
                                    'left so its callout can land beside it". This is what ' +
                                    'makes a phase a rearrangement rather than another entrance. ' +
                                    'Required on every phase after the first, unless that phase ' +
                                    'says what goes in exits.',
                            ),
                        exits: z
                            .string()
                            .optional()
                            .describe(
                                'What leaves in this phase. The default for anything that has ' +
                                    'made its point: once the narration has moved past a line or ' +
                                    'figure, it goes, rather than shrinking to stay on screen. ' +
                                    'Keep one only when what arrives makes no sense without it — ' +
                                    'the chart a callout points at, the figure a comparison is ' +
                                    'measured against. A headline or line the narration has ' +
                                    'already said is never that: it leaves rather than shrinking ' +
                                    'into a header.',
                            ),
                    }),
                )
                .describe(
                    'When the graphic advances, in order — its pacing across the scene. A phase ' +
                        'is not just an entrance: something arrives, and what has made its point ' +
                        'leaves or, when the arrival is read against it, transforms to make room. ' +
                        'A longer scene buys more phases of the SAME idea (a setup, the thing ' +
                        'that lands it, what it means), never a second idea. Roughly one phase ' +
                        'per 5s of scene, spread across it so the last one lands late, with its ' +
                        'move finished a second before the cut so its result can be taken in: a ' +
                        'schedule that resolves early leaves a stretch with nothing happening, ' +
                        'which is rejected. Only the first phase may simply arrive — every later ' +
                        'one says what the arrival costs the frame in exits or transforms, or it ' +
                        'is rejected.',
                ),
        })
        .describe('What the graphic is, and when it advances, across the whole scene.'),
});
export type WritePlanArgs = z.infer<typeof writePlanSchema>;

const SCENE_PLAN_FIELDS = Object.keys(writePlanSchema.shape.scenePlan.shape);

/**
 * Moves plan fields sent beside `scenePlan` into it. Models regularly nest the plan one level off —
 * `text` next to `scenePlan`, or the whole plan flattened onto the arguments — and, told exactly
 * what is missing, spend a round trip resending the same plan. None of these names means anything
 * at the top level, and a field the plan already carries is left as sent.
 */
function nestPlanFields(args: Record<string, unknown>): Record<string, unknown> {
    const misplaced = SCENE_PLAN_FIELDS.filter((field) => field in args);
    if (misplaced.length === 0) {
        return args;
    }
    const sent = args.scenePlan;
    const scenePlan: Record<string, unknown> =
        sent && typeof sent === 'object' && !Array.isArray(sent) ? { ...sent } : {};
    const rest = { ...args };
    for (const field of misplaced) {
        if (!(field in scenePlan)) {
            scenePlan[field] = rest[field];
        }
        delete rest[field];
    }
    return { ...rest, scenePlan };
}

const VOICEOVER_TOLERANCE_RATIO = 0.15;
const VOICEOVER_TOLERANCE_FLOOR_MS = 500;

function voiceoverTolerance(sceneDurationMs: number): number {
    return Math.max(VOICEOVER_TOLERANCE_FLOOR_MS, sceneDurationMs * VOICEOVER_TOLERANCE_RATIO);
}

// The agent is told to branch on `approved`, so a rejection has to arrive in that shape rather than
// as prose it would have to interpret.
function denied(reason: string): EnhancedToolResult {
    return { result: JSON.stringify({ approved: false, reason }), isSuccess: false };
}

/**
 * Casing and punctuation only, so "6 WEEKS" and "6 weeks" are one line. A figure repeated inside a
 * longer line — "6 WEEKS" against "6 WEEKS TO PREPARE" — reads as different copy here and is not
 * reported; matching on contained figures would flag a value against every line that mentions it.
 */
function normalize(line: string): string {
    return line
        .toLowerCase()
        .replace(/[^a-z0-9%]+/g, ' ')
        .trim();
}

/**
 * Copy this plan puts on screen that an earlier graphic already shows. Not grounds for rejection —
 * a deliberate callback or a recurring header is the same shape — but nothing else in the build
 * sees it: every other check runs inside one graphic.
 */
function copyAlreadyPlanned(text: string[], earlier: ReadonlyArray<{ text: string[] }>): string[] {
    const seen = new Set(
        earlier
            .flatMap((plan) => plan.text)
            .map(normalize)
            .filter(Boolean),
    );
    return text.filter((line) => seen.has(normalize(line)));
}

function echoedCopyNote(echoed: string[]): string {
    if (echoed.length === 0) {
        return '';
    }
    const list = echoed.map((line) => `"${line}"`).join(', ');
    return (
        `${list} already appears on another graphic in this video, so the viewer reads it twice. ` +
        `Keep it only if the repeat is the point — a callback, or a header the video returns to. ` +
        `Otherwise cut it here and let the graphic that owns it carry it.`
    );
}

export interface WritePlanToolArgs extends ToolConstructorArgs {
    /**
     * The messages the next request will send — after compaction and truncation, not the raw
     * history — read to tell which assigned techniques the model still has in front of it.
     */
    getConversationMessages?: () => ConversationMessage[];
}

export class WritePlanTool extends DesignTool<typeof writePlanSchema> {
    private readonly getConversationMessages?: () => ConversationMessage[];

    public constructor(args: WritePlanToolArgs) {
        super(args);
        this.getConversationMessages = args.getConversationMessages;
    }

    protected override readonly schema = writePlanSchema;
    protected override readonly toolName = TOOL_NAMES.writePlan;
    protected override readonly toolDescription =
        'Submit your plan for the graphic on a scene and have it reviewed. You MUST call ' +
        `this for a scene and get an approved plan before you call ${TOOL_NAMES.writeHtmlAsset} ` +
        'for that scene. ' +
        'Returns {"approved": true|false, "notes": "...", "reason": "...", "techniques": {...}}. When ' +
        'approved is false the plan is rejected — read "reason", revise, and call again; ' +
        'do not start authoring. When approved is true, "notes" may carry advice worth ' +
        'applying (often blank), and "techniques.required" names the one or two design & motion ' +
        'skills this graphic is to be built from — an assignment, not a list of options. ' +
        `${TOOL_NAMES.loadSkill} the ones "techniques.instruction" asks you to load, implement ` +
        `them all, and only then write the HTML with ` +
        `${TOOL_NAMES.writeHtmlAsset}. ` +
        'sceneDurationMillis is how long the whole scene should play.';

    public getToolDisplayName(): string {
        return 'Planning the graphic';
    }

    protected override preprocessArgs(args: Record<string, unknown>): Record<string, unknown> {
        return nestPlanFields(args);
    }

    public get statusIcon(): string {
        return 'draft';
    }

    public async execute(
        args: WritePlanArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const resolved = this.resolveTarget(args.scene_id);
        if ('error' in resolved) {
            return resolved.error;
        }
        const { scene } = resolved;

        const { sceneDurationMillis } = args;
        if (!Number.isFinite(sceneDurationMillis) || sceneDurationMillis <= 0) {
            return denied('sceneDurationMillis must be a positive number of milliseconds.');
        }

        const voicedriven = isVoicedrivenScene(scene);
        if (voicedriven) {
            const drift = Math.abs(sceneDurationMillis - scene.duration);
            if (drift > voiceoverTolerance(scene.duration)) {
                return denied(
                    `This scene's duration is fixed at ${scene.duration}ms by its voiceover, ` +
                        `and the plan is built for ${sceneDurationMillis}ms. The timing isn't ` +
                        `yours to choose here — re-plan the graphic to fill ${scene.duration}ms, ` +
                        `and pass that as sceneDurationMillis. ` +
                        `If that leaves more time than the content needs, spend it developing what ` +
                        `is already on screen rather than adding to it.`,
                );
            }
        }

        // Against the duration the graphic will really play for: a voicedriven scene keeps its own,
        // which the tolerance above allows to differ slightly from what the plan was built for.
        // Checked before the duration is written below, so a denied plan leaves the scene untouched.
        const scheduleFor = voicedriven ? scene.duration : sceneDurationMillis;
        const rejection = scheduleRejection(args.scenePlan.phases, scheduleFor / 1000);
        if (rejection) {
            return denied(rejection);
        }

        if (!voicedriven) {
            // Mirrors set_scene_duration: mark it a user override so a later text-driven
            // recommended-duration recalc doesn't reset the value the plan was built against.
            setSceneDuration(this.luminary, scene.id, sceneDurationMillis);
            this.dispatch(
                setSceneDurationUserOverride({
                    sceneId: scene.id,
                    isAutomatic: false,
                    systemTriggered: false,
                    parentActionGroup: context.parentActionGroup,
                }),
            );
        }

        // Read before this plan is stored, so the scene is not compared against itself.
        const echoed = copyAlreadyPlanned(
            args.scenePlan.text,
            getEarlierScenePlans(this.getState(), scene.id),
        );

        const techniques = await this.assignTechniques(args, scene.id, context);

        // After the assignment, so the store holds only earlier scenes while it is made. Remembered
        // so the composition the agent then authors can be checked against it: unplanned copy is
        // reported as a blocking finding by write_html_asset and edit_html_asset.
        this.dispatch(
            setHtmlDesignPlan({
                sceneId: scene.id,
                plan: args.scenePlan,
                techniques: techniques.required.map(({ skill }) => skill),
            }),
        );
        // An approved plan is where building starts, so the editor dims the scene from here.
        markScenesBusy(this.dispatch, [scene.id]);

        return {
            result: JSON.stringify({ approved: true, notes: echoedCopyNote(echoed), techniques }),
            isSuccess: true,
        };
    }

    private async assignTechniques(
        args: WritePlanArgs,
        sceneId: string,
        context: ToolExecutionContext,
    ): Promise<TechniqueAssignment> {
        const state = this.getState();
        const input = {
            sceneDurationMillis: args.sceneDurationMillis,
            alreadyAssigned: getAssignedTechniques(state, sceneId),
            phaseTimes: args.scenePlan.phases.map(({ at }) => at),
            drawsObject: args.scenePlan.graphics.length > 0,
            alreadyLoaded: getSkillsInContext(this.getConversationMessages?.() ?? []),
        };
        const chosen = context.model
            ? await chooseTechniques(
                  args.scenePlan,
                  shortlist(input),
                  getEarlierScenePlans(state, sceneId),
                  context.model,
                  context.abortSignal,
              )
            : [];
        return chosen.length > 0 ? assignmentFor(chosen, input) : fallbackAssignment(input);
    }
}
